/**
 * TypeScript Types for Delivery Management Module
 * Used for API communication and state management
 * Enhanced to match Transport module's feature set
 */

// Transport-style delivery statuses with stage progression
export type DeliveryStatus =
  | 'REQUEST_CREATED'
  | 'BROADCASTING'
  | 'ASSIGNED'
  | 'ACCEPTED'
  | 'PENDING'
  | 'SCHEDULED'
  | 'RESCHEDULED'
  | 'IN_TRANSIT'
  | 'ARRIVED_PICKUP'
  | 'PICKED_UP'
  | 'ARRIVED_DROP'
  | 'DELIVERED'
  | 'PAYMENT_PENDING'
  | 'PAYMENT_RECEIVED'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

export interface Delivery {
  id: number;  // BIGINT from backend
  externalId?: string;
  deliveryNumber?: string;
  requestNumber?: string;  // Transport-style request number (e.g., TR-null-991785-456238)
  orderNumber?: string;    // Sales order number (e.g., ORD-20260108-64451)
  salesOrderId?: number;   // May be null for Transport-only deliveries
  status: DeliveryStatus;

  // Stage tracking (1-5 scale like transport)
  stage?: number;

  // Legacy single address field
  deliveryAddress?: string;

  // Pickup location details (Transport-style)
  pickupAddress?: string;
  pickupCity?: string;
  pickupState?: string;
  pickupPincode?: string;
  pickupContactName?: string;
  pickupContactPhone?: string;

  // Drop/Delivery location details (Transport-style)
  dropAddress?: string;
  dropCity?: string;
  dropState?: string;
  dropPincode?: string;
  dropContactName?: string;
  dropContactPhone?: string;

  // Driver & Vehicle info
  driverId?: string;
  driverName?: string;
  driverPhone?: string;
  vehicleId?: string;
  vehicleNumber?: string;
  vehicleType?: string;

  // Priority & urgency
  priority?: 'LOW' | 'NORMAL' | 'MEDIUM' | 'HIGH' | 'URGENT';
  isUrgent?: boolean;

  // Amount info
  estimatedAmount?: number;
  finalAmount?: number;

  // Product info
  productType?: string;
  quantity?: number;
  quantityUnit?: string;

  // Distance info
  estimatedDistanceKm?: number;

  // Additional fields
  notes?: string;
  deliveredQuantity?: number;
  scheduledDate?: string;
  actualDeliveryDate?: string;
  currentLocation?: string;
  statusReason?: string;
  completedAt?: string;
  completedBy?: string;
  proofOfDelivery?: string;
  contactPerson?: string;
  contactPhone?: string;
  specialInstructions?: string;
  assignmentMode?: string;

  // Customer/Buyer name - always stores the customer name, separate from driver info
  customerName?: string;

  // Track delivery source
  assignedFrom?: 'UrjaVyapar' | 'Transport';

  createdAt?: string;
  updatedAt?: string;
}

export interface CreateDeliveryRequest {
  salesOrderId: number;
  status?: string;
  deliveryAddress?: string;
  vehicleNumber?: string;
  driverName?: string;
  driverPhone?: string;
  notes?: string;
  contactPerson?: string;
  contactPhone?: string;
  assignedFrom: 'UrjaVyapar' | 'Transport';
}

export interface UpdateDeliveryRequest {
  status?: string;
  deliveryAddress?: string;
  vehicleNumber?: string;
  driverName?: string;
  driverPhone?: string;
  notes?: string;
  currentLocation?: string;
  statusReason?: string;
  proofOfDelivery?: string;
  contactPerson?: string;
  contactPhone?: string;
}

export interface DeliveryFilterParams {
  status?: string;
  assignedFrom?: 'UrjaVyapar' | 'Transport';
  search?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  size?: number;
}

export interface DeliveryListResponse {
  content: Delivery[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface DeliveryStats {
  total: number;
  byStatus: Record<string, number>;
  bySource: Record<'UrjaVyapar' | 'Transport', number>;
}

// Transport-style status progression with stages
export const DELIVERY_STATUSES = [
  'REQUEST_CREATED',
  'BROADCASTING',
  'ASSIGNED',
  'ACCEPTED',
  'PENDING',
  'SCHEDULED',
  'RESCHEDULED',
  'IN_TRANSIT',
  'ARRIVED_PICKUP',
  'PICKED_UP',
  'ARRIVED_DROP',
  'DELIVERED',
  'PAYMENT_PENDING',
  'PAYMENT_RECEIVED',
  'COMPLETED',
  'FAILED',
  'CANCELLED'
] as const;

export const DELIVERY_SOURCES = ['UrjaVyapar', 'Transport'] as const;

export const PRIORITY_LEVELS = ['LOW', 'NORMAL', 'MEDIUM', 'HIGH', 'URGENT'] as const;

// Map status to stage number (1-5) - matching transport module
export const getStageFromStatus = (status: string): number => {
  const upperStatus = status?.toUpperCase() || '';
  const stageMap: Record<string, number> = {
    'NEW': 1,
    'PENDING': 1,
    'REQUEST_CREATED': 1,
    'BROADCASTING': 1,
    'SCHEDULED': 1,
    'RESCHEDULED': 1,
    'ASSIGNED': 2,
    'ACCEPTED': 2,
    'IN_TRANSIT': 3,
    'ARRIVED_PICKUP': 3,
    'PICKED_UP': 3,
    'ARRIVED_DROP': 4,
    'DELIVERED': 4,
    'PAYMENT_PENDING': 4,
    'PAYMENT_RECEIVED': 5,
    'COMPLETED': 5,
    'FAILED': 0,
    'CANCELLED': 0,
  };
  return stageMap[upperStatus] ?? 1;
};

// Stage labels for display
export const STAGE_LABELS: Record<number, string> = {
  0: 'Cancelled/Failed',
  1: 'Request Created',
  2: 'Assigned',
  3: 'In Transit',
  4: 'Delivered',
  5: 'Completed',
};
